import { router } from '@inertiajs/react'
import { Mic, MicOff, Video, VideoOff, Maximize2, PhoneOff } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { usePersistentCall } from '@/lib/persistent-call'
import { cn } from '@/lib/utils'

// Floating mini-window for an active call. Mounted at the React root
// (inside PersistentCallProvider) so it stays alive across Inertia
// page navigation. Visible only when the call is minimized.
export function LiveCallFloater() {
  const { active, call, isMinimized, maximize, detach } = usePersistentCall()

  // Pick the FIRST remote peer to render in the floater (1:1 → other
  // person; live → host for viewers, viewer's own video for host alone).
  const firstRemote = call.remotePeers[0] ?? null

  if (!active || !isMinimized) return null

  // Callback refs are critical here: useEffect with [stream] doesn't
  // re-fire on the first mount of the video element if the stream
  // reference hasn't changed since the floater was hidden. Callback
  // refs bind srcObject every time the element mounts.
  const bindLocal  = (el: HTMLVideoElement | null) => { if (el) el.srcObject = call.localStream }
  const bindRemote = (el: HTMLVideoElement | null) => { if (el) el.srcObject = firstRemote?.stream ?? null }
  const bindAudio  = (el: HTMLAudioElement | null) => { if (el) el.srcObject = firstRemote?.stream ?? null }

  const showRemote = !!firstRemote && firstRemote.videoOn
  const showLocal  = !!call.localStream && call.camEnabled

  function expand() {
    maximize()
    router.visit(`/calls/${active!.roomKey}`)
  }

  return (
    <div className="fixed bottom-4 right-4 w-72 max-w-[calc(100vw-2rem)] z-50 rounded-card-lg overflow-hidden bg-zinc-900 text-white shadow-2xl ring-1 ring-white/10">
      {/* Always mount the remote audio so audio plays in the floater */}
      <audio ref={bindAudio} autoPlay />

      {/* Video tile — object-contain so 16:9 source isn't cropped on the
          floater's narrower aspect. The corner self-PIP below stays
          object-cover because it's a tight selfie thumbnail. */}
      <div className="relative aspect-video bg-black">
        {showRemote ? (
          <video ref={bindRemote} autoPlay playsInline className="w-full h-full object-contain" />
        ) : showLocal ? (
          <video ref={bindLocal} autoPlay playsInline muted className="w-full h-full object-contain scale-x-[-1]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Avatar className="h-16 w-16">
              <AvatarImage src={active.host.avatar_url ?? undefined} alt={active.host.display_name} />
              <AvatarFallback>{active.host.display_name.charAt(0)}</AvatarFallback>
            </Avatar>
          </div>
        )}
        {/* Self-PIP overlay when remote shown */}
        {showRemote && showLocal && (
          <div className="absolute bottom-2 right-2 w-20 aspect-video rounded-card-sm overflow-hidden border border-white/30 bg-black">
            <video ref={bindLocal} autoPlay playsInline muted className="w-full h-full object-cover scale-x-[-1]" />
          </div>
        )}
        <div className="absolute top-1.5 left-2 flex items-center gap-1 px-2 py-0.5 rounded-pill bg-red-500 text-white text-[10px] font-semibold uppercase tracking-wide">
          <span className="h-1 w-1 rounded-full bg-white animate-pulse" /> Live
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-1 px-2 py-1.5 bg-zinc-900">
        <div className="flex items-center gap-1.5 min-w-0">
          <Avatar className="h-5 w-5">
            <AvatarImage src={active.host.avatar_url ?? undefined} alt={active.host.display_name} />
            <AvatarFallback>{active.host.display_name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-xs truncate max-w-[120px]">{active.title}</span>
        </div>
        <div className="flex items-center gap-1">
          {call.localStream && (
            <Button size="icon" variant="ghost" onClick={call.toggleMic} aria-label="Toggle mic" className={cn('h-7 w-7 text-white', !call.micEnabled && 'text-red-400')}>
              {call.micEnabled ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5" />}
            </Button>
          )}
          {call.localStream && (
            <Button size="icon" variant="ghost" onClick={call.toggleCam} aria-label="Toggle cam" className={cn('h-7 w-7 text-white', !call.camEnabled && 'text-red-400')}>
              {call.camEnabled ? <Video className="h-3.5 w-3.5" /> : <VideoOff className="h-3.5 w-3.5" />}
            </Button>
          )}
          <Button size="icon" variant="ghost" onClick={expand} aria-label="Expand" className="h-7 w-7 text-white">
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="icon"
            variant="destructive"
            onClick={() => {
              // The floater's End-call button is a single tap on a small
              // affordance — easy to hit by accident. Confirm before
              // tearing down the call (esp. for hosts, where this also
              // ends the broadcast for everyone).
              const ok = window.confirm('End the call?')
              if (ok) detach()
            }}
            aria-label="End call"
            className="h-7 w-7"
          >
            <PhoneOff className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
